// src/components/Menu.js
import React, { useState, useEffect, useContext } from 'react';
import { CartContext } from '../context/CartContext';
import AdBanner from './AdBanner';

const MenuItem = ({ item, onAddToCart, isFavorited, onToggleFavorite }) => {
    const [isAdded, setIsAdded] = useState(false);

    const handleAddToCartClick = () => {
        onAddToCart(item, () => {
            setIsAdded(true);
        });
    };

    useEffect(() => {
        if (isAdded) {
            const timer = setTimeout(() => setIsAdded(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [isAdded]);

    const description = item.description || `Delicious ${item.name}, freshly prepared.`;
    const itemTypeIconClass = item.item_type === "veg" ? "veg" : (item.item_type === "non-veg" ? "non-veg" : "");
    const buttonClass = `add-to-order-btn ${isAdded ? 'item-added-feedback' : ''}`;
    
    let displayPrice;
    if (item.sizes && item.sizes.length > 1) {
        displayPrice = <>
            <span className="price-label">Starts at</span>
            <span className="price-value">₹{parseFloat(item.sizes[0].price).toFixed(2)}</span>
        </>;
    } else if (item.sizes && item.sizes.length === 1) {
        displayPrice = `₹${parseFloat(item.sizes[0].price).toFixed(2)}`;
    } else {
        displayPrice = 'N/A';
    }

    return (
        <div className="menu-item-card">
            <div className="item-content">
                <div className="item-image-container">
                    {/* --- THIS IS THE FIX FOR THE BROKEN IMAGE --- */}
                    <img 
                        src={item.image_url} 
                        alt={item.name} 
                        className="food-image" 
                        loading="lazy"
                        onError={(e) => {
                            e.target.onerror = null; // Prevents looping if the placeholder also fails
                            e.target.src = `https://placehold.co/80x80/EFEFEF/AAAAAA?text=Image`;
                        }}
                    />
                </div>
                <div className="item-text-content">
                    <div className="item-header">
                        <h3>
                            {itemTypeIconClass && <span className={`item-type-indicator ${itemTypeIconClass}`}></span>}
                            {item.name}
                        </h3>
                        <span className="price">{displayPrice}</span>
                    </div>
                    <p className="description">{description}</p>
                    <div className="item-tags">
                        {item.is_popular && <span className="tag popular">Popular</span>}
                        {item.is_recommended && <span className="tag recommended">Recommended</span>}
                    </div>
                </div>
            </div>
            <div className="add-to-order-wrapper">
                <button className={buttonClass} onClick={handleAddToCartClick} disabled={isAdded}>
                    <span className="add-to-order-text">
                        {isAdded ? <>Added <i className="fas fa-check"></i></> : '+ Add to Order'}
                    </span>
                </button>
                <button
                    className={`favorite-btn ${isFavorited ? 'favorited' : ''}`}
                    onClick={() => onToggleFavorite(item.item_id)}
                    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                    <i className="fas fa-heart"></i>
                </button>
            </div>
        </div>
    );
};


const Menu = ({
    menuData,
    activeCategory,
    onCategoryScroll,
    showOnlyVeg,
    onVegToggle,
    showOnlyFavorites,
    onToggleFavorites,
    onAddToCart,
    searchTerm,
    inMenuFirstAd,
    inMenuSecondAd,
    restaurantId,
    fingerprint
}) => {
    const { favorites, toggleFavorite } = useContext(CartContext);
    
    useEffect(() => {
        if (searchTerm || showOnlyFavorites) return;

        const observer = new IntersectionObserver((entries) => {
            // Find the intersecting entry that is closest to the top
            let active = null;
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    active = entry.target.id.replace('category-', '').replace(/-/g, ' ');
                }
            });
            if (active && onCategoryScroll) {
                onCategoryScroll(active);
            }
        }, {
            rootMargin: '-100px 0px -60% 0px', // Trigger when header reaches upper-mid screen
            threshold: 0
        });

        const sections = document.querySelectorAll('.menu-category-section');
        sections.forEach(section => observer.observe(section));

        return () => sections.forEach(section => observer.unobserve(section));
    }, [menuData, searchTerm, showOnlyFavorites, onCategoryScroll]);
    
    let title;
    if (searchTerm) {
        title = `Search Results for "${searchTerm}"`;
    } else if (showOnlyFavorites) {
        title = 'Your Favorites';
    } else {
        title = 'Menu'; // Title is just Menu now, because all categories are visible
    }

    return (
        <main className="menu-section">
            <div className="menu-section-header">
                <h2 id="current-category-title">{title}</h2>
                <div className="filters-container">
                    <div className="veg-toggle-container">
                         <label className="veg-toggle-switch">
                            <input type="checkbox" checked={showOnlyFavorites} onChange={onToggleFavorites} />
                            <span className="slider"></span>
                        </label>
                        <span className="veg-toggle-label">Favorites</span>
                    </div>
                    <div className="veg-toggle-container">
                        <label className="veg-toggle-switch">
                            <input type="checkbox" id="veg-only-toggle" checked={showOnlyVeg} onChange={onVegToggle} />
                            <span className="slider"></span>
                        </label>
                        <span className="veg-toggle-label">Veg</span>
                    </div>
                </div>
            </div>
            <span className="section-title-underline"></span>
            
            <div id="menu-items-wrapper">
                {menuData.length > 0 ? (
                    menuData.map((category, index) => (
                        <React.Fragment key={category.name}>
                            <div 
                                id={`category-${category.name.replace(/\s+/g, '-')}`} 
                                className="menu-category-section"
                                style={{ scrollMarginTop: 'calc(var(--header-height, 80px) + 70px)', marginBottom: '40px' }}
                            >
                                <h3 style={{ marginBottom: '20px', color: 'var(--text-color-headings)', fontSize: '1.4rem' }}>
                                    {category.name}
                                </h3>
                                <div className="menu-grid">
                                    {category.items.map(item => (
                                        <MenuItem 
                                            key={item.item_id} 
                                            item={item} 
                                            onAddToCart={onAddToCart}
                                            isFavorited={favorites.includes(item.item_id)}
                                            onToggleFavorite={toggleFavorite}
                                        />
                                    ))}
                                </div>
                            </div>
                            
                            {index === 0 && inMenuFirstAd && (
                                <AdBanner ad={inMenuFirstAd} restaurantId={restaurantId} fingerprint={fingerprint} />
                            )}
                            
                            {index === 2 && inMenuSecondAd && (
                                <AdBanner ad={inMenuSecondAd} restaurantId={restaurantId} fingerprint={fingerprint} />
                            )}
                        </React.Fragment>
                    ))
                ) : (
                    <p>No items match your current filters.</p>
                )}
            </div>
        </main>
    );
};

export default Menu;
